import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "./app/hooks";
import {
  selectCurrentUser,
  selectUserCognitoGroups
} from "./features/Users/usersSlice";
import { CognitoUserGroups } from "./features/Users/userUtils";
import { AuthNotifications, useAuth } from "./features/Auth/authUtilities";
import { CircularProgress, Typography } from "@mui/material";
import { SignOutButton, handleSignOut } from "./common-ui/SignOutButton";
import { Outlet, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { BrandingContext } from "./common-ui/BrandingContext";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import { MessageCenter } from "./features/MessageQueue/MessageCenter";
import { useMessageQueue } from "./features/MessageQueue/useMessageQueue";
import { EulaGate } from "./features/UserInvitations/EulaGate";

const INACTIVITY_TIMEOUT_SECONDS = 60 * 15;

export const DefaultHome = () => {
  const { t } = useTranslation();
  const { user: amplifyUser } = useAuthenticator((context) => [context.user]);
  const { status: authStatus, error: authError } = useAuth({ amplifyUser });
  const branding = useContext(BrandingContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signOut } = useAuthenticator();
  const { hasBlocking } = useMessageQueue();

  const currentAuthenticatedUser = useAppSelector(selectCurrentUser);
  const cognitoGroups = useAppSelector(selectUserCognitoGroups);

  const necessaryStatuses = [authStatus, branding.status];

  useIdleTimer({
    crossTab: true,
    syncTimers: 1000,
    timeout: 1000 * INACTIVITY_TIMEOUT_SECONDS,
    onIdle: () => {
      if (currentAuthenticatedUser) {
        handleSignOut({ dispatch, signOut, navigate });
      }
    },
    debounce: 1000
  });

  if (necessaryStatuses.every((status) => status === "idle")) {
    return <Typography variant="h2">{t("Awaiting data...")}</Typography>;
  }

  if (necessaryStatuses.includes("loading")) {
    return <CircularProgress />;
  }

  if (authStatus === "failed") {
    return (
      <>
        <Typography variant="h2">{t("Error")}</Typography>
        <Typography variant="h3">
          {t(AuthNotifications.FetchUserError)}
        </Typography>
        {authError && <Typography>{authError}</Typography>}
      </>
    );
  }

  // If the user is logged in but doesn't have an associated user in the database,
  // show an appropriate error.
  if (authStatus === "succeeded" && !currentAuthenticatedUser) {
    return (
      <div>
        {t(AuthNotifications.NoDDBUser)}
        <SignOutButton />
      </div>
    );
  }

  if (necessaryStatuses.every((status) => status === "succeeded")) {
    // Proceed with routing if the user has necessary permissions
    if (
      cognitoGroups?.some((group) =>
        Object.values(CognitoUserGroups).includes(group)
      )
    ) {
      return (
        <EulaGate>
          <MessageCenter />
          {!hasBlocking && <Outlet />}
        </EulaGate>
      );
    }

    // If we get here and the user's cognito permission hasn't been found, they don't
    // have ANY permissions.
    return (
      <div>
        {t("Current user has no permissions.")}
        <SignOutButton />
      </div>
    );
  }

  return null;
};
